import { useEffect } from 'react'

/**
 * Locks page scroll while `isLocked` is set, restoring the previous scroll position on release.
 *
 * A native <dialog> opened with showModal() makes the page behind it inert but does NOT stop it
 * scrolling, so any full-screen dialog needs this too.
 *
 * `overflow: hidden` alone would be enough to stop the scrolling, but it also makes the scrollbar
 * disappear, which widens the viewport and shifts the whole page — including anything positioned
 * against it — by the scrollbar's width. Padding the body by that same width cancels the shift.
 * Pinning the body with `position: fixed` and a negative offset keeps the lock working on iOS,
 * where `overflow: hidden` on the body is ignored.
 */
export const useBodyScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    if (!isLocked) return

    const scrollY = window.scrollY
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    const originalPosition = document.body.style.position
    const originalTop = document.body.style.top
    const originalWidth = document.body.style.width
    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight

    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(getComputedStyle(document.body).paddingRight) || 0
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`
    }

    return () => {
      document.body.style.position = originalPosition
      document.body.style.top = originalTop
      document.body.style.width = originalWidth
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
      window.scrollTo(0, scrollY)
    }
  }, [isLocked])
}
